import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerModule } from '@coreui/angular-pro';
import { TranslatePipe, I18nService } from 'i18n';
import { Logger } from 'shared';
import { CrossLoginService } from '../../../services/cross-login.service';

/**
 * Landing page for /auth/handoff/:token on the TARGET site of a cross-login.
 * Exchanges the single-use handoff token for a session, then forwards the user
 * to their dashboard (or back to login on failure).
 */
@Component({
  selector: 'app-cross-login-handoff-page',
  standalone: true,
  imports: [CommonModule, SpinnerModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="d-flex flex-column align-items-center justify-content-center text-center"
         style="min-height: 60vh;">
      @if (error()) {
        <p class="text-danger mb-0">{{ 'cross_login_failed' | translate }}</p>
      } @else {
        <c-spinner color="primary"></c-spinner>
        <p class="mt-3 mb-0">{{ 'cross_login_signing_in' | translate }}</p>
      }
    </div>
  `
})
export class CrossLoginHandoffPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private crossLogin = inject(CrossLoginService);
  private i18n = inject(I18nService);
  private logger = new Logger('CrossLoginHandoffPage');

  error = signal(false);

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.fail();
      return;
    }
    this.crossLogin.consume(token).subscribe({
      next: () => this.router.navigate(['/', this.lang(), 'dashboard']),
      error: err => {
        this.logger.error('Cross-login consume failed', err);
        this.fail();
      }
    });
  }

  private lang(): string {
    return this.i18n.getLanguage?.() || localStorage.getItem('language') || 'en';
  }

  private fail(): void {
    this.error.set(true);
    setTimeout(() => this.router.navigate(['/', this.lang(), 'login']), 1500);
  }
}
